import ROUTES from "../../router/routes.constants";
import { formatPercentage, formatIndianNumberWithDecimals } from "../../utils/currency.utils";
import { calculateWeightedAverage } from "../../utils/calculation";
import { getLogoUrl } from "../../utils/getLogoUrl";
import { formatDate } from "../../utils/date.utils";

/**
 * Parser for FinFactor dashboard overview.
 * Converts API schema structure to UI expected format.
 * 
 * @param {Object} data - API response result object
 * @param {Object} portfolioSummary - Portfolio summary object (optional)
 * @returns {Object} Parsed overview data for UI
 */
export const parseDashboardOverview = (data, portfolioSummary = null) => {
    if (!data) return null;

    const portfolioValue = Number(String(portfolioSummary?.currentValue || 0).replace(/,/g, '')) || 0;
    const aaWealth = Number(String(data?.totalWealth || 0).replace(/,/g, '')) || 0;
    const totalWealth = aaWealth + portfolioValue;

    const lastUpdatedAt = data?.lastUpdatedAt; // "2026-03-23T13:00:24.000Z"
    const summary = data?.summary || {};

    const aaInvestmentsValue = Number(String(summary?.investments?.value || 0).replace(/,/g, '')) || 0;
    const combinedInvestmentsValue = aaInvestmentsValue + portfolioValue;

    const investmentsPercent = summary?.investments?.percent ?? 0;
    const banksPercent = summary?.banks?.percent ?? 0;

    return {
        wealth: {
            title: "Financial Overview",
            totalWealthLabel: "Total Wealth",
            totalWealth: totalWealth ?? 0,
            formattedWealth: totalWealth != null ? `₹${formatIndianNumberWithDecimals(totalWealth)}` : "-",
            subtitle: "Based on your connected accounts.",
            lastUpdated: lastUpdatedAt ?? null, // ← raw timestamp pass karo, format mat karo yahan
            wealthColor: "#38bdf8"
        },
        chartData: [
            { category: "Investments", value: investmentsPercent, color: "#783daa" },
            { category: "Banks", value: banksPercent, color: "#b07edb" },
            { category: "Insurance", value: summary?.insurance?.percent ?? 0, color: "#b07edb" },
        ],
        accounts: [
            {
                id: 1,
                title: "Investments",
                value: (combinedInvestmentsValue > 0 || summary?.investments?.value != null)
                    ? `₹${formatIndianNumberWithDecimals(combinedInvestmentsValue)}`
                    : "-",
                subtitle: `${Math.max(summary?.investments?.assetTypes || 0, portfolioValue > 0 ? 1 : 0)} asset type${Math.max(summary?.investments?.assetTypes || 0, portfolioValue > 0 ? 1 : 0) === 1 ? "" : "s"}`,
                icon: "investment",
                route: ROUTES.INVESTMENT_PORTFOLIO
            },
            {
                id: 2,
                title: "Banks",
                value: summary?.banks?.value != null ? `₹${formatIndianNumberWithDecimals(summary.banks.value)}` : "-",
                subtitle: `${summary?.banks?.accounts ?? 0} Accounts`,
                icon: "bank",
                route: ROUTES.FINFACTOR_BANKING,
                // status: "comingSoon"
            },
            ...(summary?.insurance?.coverage || summary?.insurance?.policies ? [{
                id: 3,
                title: "Insurance",
                value: summary?.insurance?.coverage
                    ? `₹${Math.floor(summary.insurance.coverage / 100000)}L Coverage`
                    : "-",
                subtitle: `${summary?.insurance?.policies ?? 0} Policies`,
                icon: "insurance",
                route: ROUTES.FINFACTOR_INSURANCE,
            }] : []),
            {
                id: 4,
                title: "Liabilities",
                value: "₹0",
                subtitle: "Coming Soon",
                icon: "liabilities",
                route: "/liabilities",
                status: "comingSoon"
            }
        ],
        financialGoals: {
            title: "Financial Goals",
            button: {
                text: "Add Goal"
            },
            comingSoon: true
        },
        smartInsights: {
            title: "Smart Insights & Recommendations",
            comingSoon: true
        }
    };
};

const normalizeNullValue = (value) => {
    if (value === null || value === undefined || value === "N/A" || value === "null" || String(value).trim() === "" || String(value) === "Invalid Date") {
        return "-";
    }
    return value;
};

const formatAsOnDate = (isoString) => {
    if (!isoString) return "-";
    try {
        const date = new Date(isoString);
        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
        }).replace(",", ";") + " IST";
    } catch {
        return "-";
    }
};

const normalizeNomineeStatus = (status) => {
    if (!status) return "none";
    const normalized = String(status).trim().replace(/[_-]/g, " ").toLowerCase();

    if (normalized.includes("not registered")) return "not registered";
    if (normalized.includes("registered")) return "registered";

    return "none";
};

/**
 * Formats a currency value with the Rupee symbol and handles negative sign correctly.
 * Example: -4218.8 -> -₹4,218.8
 * 
 * @param {number|string} val - Numeric value or string representing a number
 * @returns {string} Formatted currency string
 */
const formatRupeeWithSign = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const num = Number(String(val).replace(/,/g, ''));
    if (isNaN(num)) return "-";
    return `₹${formatIndianNumberWithDecimals(Math.abs(num))}`;
};

/**
 * Formats a percentage value without a sign.
 * 
 * @param {number|string} val - Numeric value or string representing a number
 * @returns {string} Formatted percentage string
 */
const formatPercentageNoSign = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const num = Number(val);
    if (isNaN(num)) return "-";
    return `${Math.abs(num).toFixed(2)}%`;
};

/**
 * Parser for Mutual Funds broker listing API response.
 * Converts API schema to UI expected format for broker listing page.
 *
 * @param {Object} data - API response result object
 * @param {Object} portfolioSummary - Portfolio summary object (optional)
 * @returns {Object|null} Parsed broker listing data for UI
 */
export const parseMutualFundsBrokerListing = (data, portfolioSummary = null) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? data?.as_on_date ?? null;
    const mutualFunds = data?.mutualFunds ?? [];

    const portfolioValue = Number(String(portfolioSummary?.currentValue || 0).replace(/,/g, '')) || 0;
    const inxitsPercent = Number(portfolioSummary?.oneDayReturnsPercentage || 0);

    // -----------------------------
    // STEP 1: Create Items
    // -----------------------------
    let items = mutualFunds.map((fund, index) => {
        const value = Number(fund?.value || 0);
        const percent = Number(fund?.growth ?? fund?.dayChangePercent ?? 0);

        return {
            id: fund?.categoryId ?? index,
            name: fund?.name ?? "Unknown",
            brokerId: fund?.categoryId ?? "",
            accountId: fund?.accountId ?? "-",
            nomineeStatus: normalizeNomineeStatus(fund?.nomineeStatus),
            value: value ? `₹${formatIndianNumberWithDecimals(value)}` : "-",
            rawValue: value,
            rawPercent: percent,
            changeValue: formatPercentageNoSign(percent),
            changeLabel: "1D",
            isPositive: percent >= 0,
            logo: getLogoUrl(fund),
        };
    });

    // -----------------------------
    // STEP 2: Add Inxits
    // -----------------------------
    if (portfolioValue > 0) {
        items.push({
            id: "inxits",
            name: "Inxits",
            brokerId: "inxits",
            accountId: "Internal",
            nomineeStatus: "registered",
            value: `₹${formatIndianNumberWithDecimals(portfolioValue)}`,
            rawValue: portfolioValue,
            rawPercent: inxitsPercent,
            changeValue: `${Math.abs(inxitsPercent).toFixed(2)}%`,
            changeLabel: "1D",
            isPositive: inxitsPercent >= 0,
        });
    }

    // -----------------------------
    // STEP 3: Sort (Inxits TOP)
    // -----------------------------
    items.sort((a, b) => {
        if (a.id === "inxits") return -1;
        if (b.id === "inxits") return 1;
        return 0;
    });

    // -----------------------------
    // STEP 4: TOTAL VALUE
    // -----------------------------
    const totalValue = items.reduce((sum, item) => sum + item.rawValue, 0);

    // -----------------------------
    // STEP 5: WEIGHTED % (FINAL FIX 🔥)
    // -----------------------------
    let weightedSum = 0;

    items.forEach(item => {
        // Only include AA items in weighted percentage if they have valid growth
        // Skip 'inxits' for total percentage to match AA reported growth (4.04) as requested
        if (item.id !== "inxits" && !isNaN(item.rawValue) && !isNaN(item.rawPercent)) {
            weightedSum += item.rawValue * item.rawPercent;
        }
    });

    const totalAAValue = items.filter(i => i.id !== "inxits").reduce((sum, item) => sum + item.rawValue, 0);
    const totalPercent = totalAAValue === 0 ? 0 : weightedSum / totalAAValue;

    // -----------------------------
    // STEP 6: RETURN FINAL DATA
    // -----------------------------
    return {
        title: "All Mutual Fund Brokers",
        asOfDate: `as on date ${formatAsOnDate(asOnDate)}`,

        totalValue: totalValue
            ? `₹${formatIndianNumberWithDecimals(totalValue)}`
            : "-",

        // 👉 USE THIS IN UI (IMPORTANT)
        totalChange: `${Math.abs(totalPercent).toFixed(2)}%`,
        isPositive: totalPercent >= 0,

        items,
    };
};


/**
 * Parser for Investment Portfolio Stocks API response.
 * Converts API schema to UI expected format for stocks listing page.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed stocks listing data for UI
 */
export const parseInvestmentPortfolioStocks = (data) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? null;
    const stocks = data?.stocks ?? [];

    const items = stocks.map((stock, index) => {
        const dayChangePercent = stock?.growth ?? stock?.dayChangePercent;

        return {
            id: stock?.categoryId ?? index,
            name: stock?.name ?? "Unknown",
            brokerId: stock?.categoryId ?? "",
            accountId: stock?.accountId ?? "-",
            nomineeStatus: normalizeNomineeStatus(stock?.nomineeStatus),
            value: stock?.value != null ? `₹${formatIndianNumberWithDecimals(stock.value)}` : "-",
            changeValue: formatPercentageNoSign(dayChangePercent),
            changeLabel: "1D",
            isPositive: dayChangePercent != null ? Number(dayChangePercent) >= 0 : null,
            logo: getLogoUrl(stock),
        };
    });

    const totalValue = data?.totalValue;

    return {
        title: "STOCKS",
        subtitle: "SEBI regulated securities & instruments",
        asOfDate: `as on date ${formatAsOnDate(asOnDate).replace(";", "")}`,
        totalValue: totalValue != null ? `₹${formatIndianNumberWithDecimals(totalValue)}` : "-",
        items,
    };
};

/**
 * Parser for Consents list API response.
 * 
 * @param {Object} data - API response result object
 * @returns {Object} Parsed consents and total count
 */
export const parseConsents = (data) => {
    if (!data) return { consents: [], totalConsents: 0, consentStatus: null };

    const consents = data?.consents || [];
    const totalConsents = data?.totalConsents || 0;
    const consentStatus = data?.consentStatus || null;

    const parsedConsents = consents.map(consent => ({
        "FI Type": consent?.fiType || "-",
        "Linked Accounts": consent?.linkedAccountsCount || "-",
    }));

    return {
        consents: parsedConsents,
        totalConsents: totalConsents,
        consentStatus: consentStatus
    };
};

/**
 * Parser for Mutual Funds broker summary API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed broker summary data for UI
 */
export const parseMutualFundsBrokerSummary = (data) => {
    if (!data) return null;

    const currentValue = data?.currentValue;
    const purchaseCost = data?.purchaseCost;
    const gain = data?.gain;
    const gainPercent = data?.gainPercent;

    return {
        name: data?.name || "Unknown",
        totalValue: currentValue != null ? `₹${formatIndianNumberWithDecimals(currentValue)}` : "-",
        change: formatRupeeWithSign(gain),
        changePercentage: formatPercentageNoSign(gainPercent),
        isPositive: gain != null ? Number(gain) >= 0 : null,
        purchaseCost: purchaseCost != null ? `₹${formatIndianNumberWithDecimals(purchaseCost)}` : "-",
        gain: formatRupeeWithSign(gain),
        gainPercentage: formatPercentageNoSign(gainPercent),
        asOnDate: data?.asOnDate,
        performanceData: (data?.performance || []).map(p => ({
            date: p.date,
            graphValue: p.value || 0
        })),
        categorizedFunds: {}
    };
};
/**
 * Parser for mutual funds details API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object} Parsed categorized funds for UI
 */
export const parseMutualFundsDetails = (data) => {
    if (!data || !data.funds) return {};

    const categorizedFunds = {};

    data.funds.forEach(category => {
        const type = category.type || "OTHER";
        categorizedFunds[type] = {
            count: (category.funds || []).length,
            totalValue: category.currentValue != null ? `₹${formatIndianNumberWithDecimals(category.currentValue)}` : "-",
            change: formatRupeeWithSign(category.dayChange),
            changePercentage: formatPercentageNoSign(category.dayChangePercent),
            purchaseCost: category.purchaseCost != null ? `₹${formatIndianNumberWithDecimals(category.purchaseCost)}` : "-",
            gain: formatRupeeWithSign(category.gainCagr),
            gainPercentage: formatPercentageNoSign(category.gainCagrPercent),
            isPositive: category.dayChange != null ? Number(category.dayChange) >= 0 : null,
            isGainPositive: category.gainCagr != null ? Number(category.gainCagr) >= 0 : null,
            isExpanded: (category.funds || []).length > 0,
            items: (category.funds || []).map((fund, index) => ({
                id: fund.isin || index,
                name: fund.name,
                logo: getLogoUrl(fund),
                isin: normalizeNullValue(fund.isin),
                folio: normalizeNullValue(fund.folioNumber ?? fund.folioNo),
                // status: "", // Removed status as per Figma
                fields: [
                    {
                        label: "Current Value",
                        value: fund.currentValue != null ? `₹${formatIndianNumberWithDecimals(fund.currentValue)}` : "-"
                    },
                    {
                        label: "Invested Amount",
                        value: fund.investedAmount != null ? `₹${formatIndianNumberWithDecimals(fund.investedAmount)}` : "-"
                    },
                    {
                        label: "Gain/ Loss",
                        value: fund.gainLoss != null && fund.gainLossPercent != null
                            ? `₹${formatIndianNumberWithDecimals(fund.gainLoss)} ${formatPercentage(fund.gainLossPercent)}`
                            : "-",
                        color: (fund.gainLoss === null || Number(fund.gainLoss) === 0)
                            ? "white"
                            : (Number(fund.gainLoss) > 0 ? "#10B981" : "#EF4444")
                    }
                ]
            }))
        };
    });

    return categorizedFunds;
};

/**
 * Sorts categorized funds based on the number of items.
 * Rules:
 * 1. Highest number of funds first.
 * 2. Categories with zero funds always at the bottom.
 * 
 * @param {Object} categorizedFunds - The categorized funds object from parseMutualFundsDetails
 * @returns {Array} Sorted entries [name, data]
 */
export const sortCategorizedFunds = (categorizedFunds) => {
    if (!categorizedFunds) return [];

    return Object.entries(categorizedFunds).sort(([, a], [, b]) => {
        const aCount = a.items?.length || 0;
        const bCount = b.items?.length || 0;

        // Rule: Categories with zero funds always at the bottom
        if (aCount === 0 && bCount > 0) return 1;
        if (bCount === 0 && aCount > 0) return -1;

        // Rule: Descending order based on funds count
        return bCount - aCount;
    });
};

const parsePercentValue = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "string") {
        const parsed = parseFloat(value.replace("%", "").trim());
        return Number.isFinite(parsed) ? parsed : 0;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeDistribution = (items, labelKey, fallbackLabel = "Unknown") => {
    const parsedItems = (items || []).map((item) => ({
        [labelKey]: item[labelKey] ?? fallbackLabel,
        value: parsePercentValue(item.value),
    }));

    return parsedItems;
};

/**
 * Parser for mutual funds asset allocation API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object} Parsed asset allocation data for UI
 */
export const parseMutualFundsAssetAllocation = (data) => {
    if (!data) return null;

    const capitalizeFirstLetter = (string) => {
        if (!string) return "";
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    };

    const byHoldingType = normalizeDistribution(
        (data.holdingType || []).map((item) => ({
            type: capitalizeFirstLetter(item.type),
            value: item.value,
        })),
        "type"
    ).map((item) => ({
        type: item.type,
        percentage: item.value
    }));

    const byCategory = normalizeDistribution(
        (data.category || []).map((item) => ({
            category: item.type,
            value: item.value,
        })),
        "category"
    );

    const byMarketCap = normalizeDistribution(
        (data.marketCap || []).map((item) => ({
            cap: item.type.replace(" Cap", ""),
            value: item.value,
        })),
        "cap"
    );

    const byAmc = normalizeDistribution(
        (data.amc || []).map((item) => ({
            amcName: item.type.replace(" Mutual Fund", ""),
            value: item.value,
        })),
        "amcName"
    );

    return {
        result: {
            byHoldingType,
            byCategory,
            byMarketCap,
            byAmc
        }
    };
};

/**
 * Parser for mutual funds transactions API response.
 *
 * @param {Object} data - API response result object
 * @returns {Array} Parsed transactions data for UI
 */
export const parseMutualFundsTransactions = (data) => {
    if (!data || !Array.isArray(data.transactions)) return [];

    const flatTransactions = [];
    data.transactions.forEach((group) => {
        const dateGroup = group?.date || "Unknown";
        if (group?.funds && Array.isArray(group.funds)) {
            group.funds.forEach((fund, index) => {
                if (!fund) return;

                const amount = fund?.amount;
                const description = ""; // Hidden as per request

                flatTransactions.push({
                    id: fund?.fundId || `${dateGroup}-${index}`,
                    fundName: fund?.name || "Unknown Fund",
                    amount: amount ?? 0,
                    formattedAmount: amount != null ? `₹${formatIndianNumberWithDecimals(amount).split(".")[0]}` : "-",
                    type: description,
                    orderType: description,
                    dateGroup: dateGroup,
                    logo: getLogoUrl(fund),
                    details: {
                        "Amount transferred on": normalizeNullValue(fund?.transactionDate),
                        "Order Id": normalizeNullValue(fund?.txnId),
                        "Nav date": normalizeNullValue(fund?.navDate),
                        "Folio no": normalizeNullValue(fund?.folioNumber ?? fund?.folioNo),
                        "Nav": fund?.nav != null ? `₹${formatIndianNumberWithDecimals(fund.nav)}` : "-",
                        "Unit": normalizeNullValue(fund?.unit),
                    },
                    steps: (fund?.statusTimeline || []).map((step, stepIndex) => ({
                        id: stepIndex + 1,
                        title: step?.activity || "Order Activity",
                        timestamp: normalizeNullValue(step?.timestamp),
                        status: (step?.status || "completed").toLowerCase()
                    })),
                    status: (fund?.status || "completed").toLowerCase()
                });
            });
        }
    });

    return flatTransactions;
};
/**
 * Parser for mutual funds transaction detail API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed transaction detail data for UI
 */
export const parseMutualFundsTransactionDetail = (data) => {
    if (!data || !data.transaction) return null;
    const { transaction } = data;

    const navDateStep = (transaction?.statusTimeline || []).find(s => s?.activity === "NAV_DATE");
    const txnDateStep = (transaction?.statusTimeline || []).find(s => s?.activity === "TRANSACTION_DATE");

    return {
        id: transaction?.fundId || "",
        fundName: transaction?.name || "Unknown Fund",
        logo: getLogoUrl(transaction),
        amount: transaction?.amount ?? 0,
        formattedAmount: transaction?.amount != null
            ? `₹${formatIndianNumberWithDecimals(transaction.amount)}`
            : "-",
        type: transaction?.type || "Transaction",
        details: {
            "Amount transferred on": normalizeNullValue(transaction?.transactionDate),
            // "Order Id": normalizeNullValue(transaction?.txnId),
            "Nav date": normalizeNullValue(transaction?.navDate),
            "Folio no": normalizeNullValue(transaction?.folioNumber),
            "Nav": transaction?.nav != null ? `₹${formatIndianNumberWithDecimals(transaction.nav)}` : "-",
            "Unit": normalizeNullValue(transaction?.unit)
        },
        steps: [
            {
                id: 1,
                title: "Order Executed Successfully",
                timestamp: normalizeNullValue(navDateStep?.timestamp),
                status: (navDateStep?.status || "Completed").toLowerCase()
            },
            {
                id: 2,
                title: "Order Placed",
                timestamp: normalizeNullValue(txnDateStep?.timestamp),
                status: (txnDateStep?.status || "Completed").toLowerCase()
            }
        ],
        status: (transaction?.status || "Executed").toLowerCase()
    };
};

/**
 * Parser for Banking accounts list.
 */
export const parseBankingAccounts = (data) => {
    if (!data) return { accounts: [], totalBalance: "₹0", totalBalanceLabel: "Total Balance" };

    return {
        title: data.title || "Linked Bank Accounts",
        totalBalanceLabel: data.totalBalanceLabel || "Total Balance",
        totalBalance: data.totalBalance != null ? `₹${formatIndianNumberWithDecimals(data.totalBalance)}` : "-",
        accounts: (data.accounts || []).map(acc => ({
            id: acc.id,
            bankName: acc.bankName || "Unknown Bank",
            accountNumber: acc.accountNumber || "XXXXXXXXXXXX",
            nominee: acc.nominee || "-",
            NomineStatus: normalizeNomineeStatus(acc.nomineeStatus),
            balance: acc.balance != null ? `₹${formatIndianNumberWithDecimals(acc.balance)}` : "-",
            lastUpdated: acc.lastUpdated || "-",
            logo: getLogoUrl(acc),
            accountType: acc.accountType || "Savings A/c"
        }))
    };
};

/**
 * Parser for Banking transactions.
 */
export const parseBankingTransactions = (data) => {
    if (!data || !Array.isArray(data.transactions)) return [];

    return data.transactions.map((tx, index) => ({
        id: tx.id || index,
        title: tx.description || tx.narration || "Transaction",
        amount: tx.amount != null ? `₹${formatIndianNumberWithDecimals(tx.amount)}` : "-",
        timestamp: tx.date || tx.timestamp || "-",
        type: (tx.type || "debit").toLowerCase(),
        status: (tx.status || "completed").toLowerCase()
    }));
};

/**
 * Parser for Insurance policies.
 */
export const parseInsurancePolicies = (data) => {
    if (!data || !Array.isArray(data.policies)) return { policies: [] };

    return {
        policies: data.policies.map((group, index) => ({
            id: index,
            category: group.category || "Unknown",
            totalSumAssured: group.totalSumAssured || "₹0",
            totalPremium: group.totalPremium || "₹0",
            icon: group.icon || "Shield",
            isExpanded: (group.items || []).length > 0,
            items: (group.items || []).map((item, i) => ({
                id: item.id || `${index}-${i}`,
                status: (item.status || "ACTIVE").toUpperCase(),
                name: item.name || "Unknown Policy",
                folio: item.folio || "-",
                isin: item.isin || "",
                fields: (item.fields || []).map(f => ({
                    label: f.label,
                    value: f.value
                }))
            }))
        }))
    };
};

/**
 * Static UI data for FinConsentContainer and other sections.
 * This replaces mock dependencies for marketing copy.
 */
export const getFinFactorStaticUI = () => ({
    myInvestments: {
        title: "My Investments",
        subtitle: "See Your Wealth the Way It Should Be Seen, Together.",
        buttonText: "Give Consent",
        features: {
            left: [
                { id: 1, label: "Bank-grade Encryption" },
                { id: 2, label: "Privacy-First Platform" },
                { id: 3, label: "No Ads. No Distractions." }
            ],
            right: [
                { id: 4, label: "Independent Security Audits" },
                { id: 5, label: "Credentials Never Stored" },
                { id: 6, label: "100% Secure Portfolio Sync" }
            ]
        }
    },
    securitySection: {
        title: "How secure is my Data?",
        subtitle: "Your data is protected with bank-grade encryption and strict access controls.",
        cards: [
            { id: 1, title: "End to End Encrypted", icon: "Encrypted", color: "#6D28D9" },
            { id: 2, title: "Bank Level Security", icon: "Security", color: "#059669" },
            { id: 3, title: "No credentials stored", icon: "Credenails", color: "#D97706" }
        ]
    }
});


/**
 * Parser for Investment Portfolio Stocks details API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed stocks details data for UI
 */
export const parseInvestmentPortfolioStocksDetails = (data) => {
    if (!data) return null;

    const marketCapColors = ["#8B5CF6", "#A855F7", "#D8B4FE"];
    const sectorColors = ["#6366F1", "#8B5CF6", "#A855F7", "#EC4899", "#3B82F6", "#F472B6"];

    return {
        name: (data?.name === "stocks" || data?.name === "Stocks") ? "STOCKS" : (data?.name ?? "Unknown"),
        accountId: data?.accountId ?? "-",
        nomineeStatus: normalizeNomineeStatus(data?.nomineeStatus),

        value:
            (data?.value != null || data?.portfolioValue != null)
                ? `₹${formatIndianNumberWithDecimals(data?.value ?? data?.portfolioValue)}`
                : "-",

        changeValue:
            (data?.growth ?? data?.dayChangePercent) != null
                ? formatPercentage(data.growth ?? data.dayChangePercent)
                : "-",

        rawChangePercent: (data?.growth ?? data?.dayChangePercent) != null ? (data?.growth ?? data?.dayChangePercent) : null,
        changeLabel: "1D",
        isPositive: (data?.growth ?? data?.dayChangePercent) != null ? Number(data?.growth ?? data?.dayChangePercent) >= 0 : null,

        marketCap: {
            totalAmount:
                data?.marketCapDistribution?.totalAmount != null
                    ? `₹${formatIndianNumberWithDecimals(data.marketCapDistribution.totalAmount)}`
                    : "-",

            data: (data?.marketCapDistribution?.data ?? []).map((item, index) => ({
                category: `${item?.type?.replace?.(/_/g, " ") ?? "Unknown"} - ₹${formatIndianNumberWithDecimals(item?.value ?? 0)}`,
                value: item?.percentage ?? 0,
                color: marketCapColors[index % marketCapColors.length]
            }))
        },

        sector: {
            totalAmount:
                data?.sectorDistribution?.totalAmount != null
                    ? `₹${formatIndianNumberWithDecimals(data.sectorDistribution.totalAmount)}`
                    : "-",

            data: (data?.sectorDistribution?.data ?? []).map((item, index) => ({
                category: `${item?.sector ?? "Unknown"} - ${item?.percentage ?? 0}%`,
                value: item?.percentage ?? 0,
                color: sectorColors[index % sectorColors.length]
            }))
        },

        holdings: (data?.holdings ?? []).map((stock) => ({
            name: stock?.name ?? "Unknown",
            isin: stock?.isin ?? "-",

            value:
                stock?.value != null
                    ? `₹${formatIndianNumberWithDecimals(stock.value)}`
                    : "-",

            change:
                stock?.dayChange != null && stock?.dayChangePercent != null
                    ? `${formatRupeeWithSign(stock.dayChange)} (${formatPercentageNoSign(stock.dayChangePercent)}) 1D`
                    : "-",

            dayChange: stock?.dayChange,
            dayChangePercent: stock?.growth ?? stock?.dayChangePercent,

            isPositive: (stock?.growth ?? stock?.dayChangePercent ?? stock?.dayChange) != null ? Number(stock?.growth ?? stock?.dayChangePercent ?? stock?.dayChange) >= 0 : null,

            closingPrice:
                stock?.closingPrice != null
                    ? `₹${formatIndianNumberWithDecimals(stock.closingPrice)}`
                    : "-",

            qty:
                stock?.quantity != null
                    ? formatIndianNumberWithDecimals(stock.quantity).split(".")[0]
                    : "-"
        }))
    };
};

/**
 * Parser for Investment Portfolio ETF details API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed ETF details data for UI
 */
export const parseInvestmentPortfolioEtfDetails = (data) => {
    if (!data) return null;

    return {
        name: (data?.name === "etf" || data?.name === "Etf") ? "ETF" : (data?.name ?? "Unknown"),
        accountId: data?.accountId ?? "-",
        nomineeStatus: normalizeNomineeStatus(data?.nomineeStatus),

        value:
            (data?.value != null || data?.portfolioValue != null)
                ? `₹${formatIndianNumberWithDecimals(data?.value ?? data?.portfolioValue)}`
                : "-",

        changeValue:
            (data?.growth ?? data?.dayChangePercent) != null
                ? formatPercentage(data.growth ?? data.dayChangePercent)
                : "-",

        rawChangePercent: (data?.growth ?? data?.dayChangePercent) != null ? (data?.growth ?? data?.dayChangePercent) : null,
        changeLabel: "1D",
        isPositive: (data?.growth ?? data?.dayChangePercent) != null ? Number(data?.growth ?? data?.dayChangePercent) >= 0 : null,

        // ETFs might not have market cap or sector distribution in the sample response,
        // but we'll provide empty structures to satisfy the UI component's expectations.
        marketCap: {
            totalAmount: "0",
            data: []
        },
        sector: {
            totalAmount: "0",
            data: []
        },

        holdings: (data?.holdings ?? []).map((etf) => ({
            name: etf?.name ?? "Unknown",
            isin: etf?.isin ?? "-",

            value:
                etf?.value != null
                    ? `₹${formatIndianNumberWithDecimals(etf.value)}`
                    : "-",

            change:
                etf?.dayChange != null && etf?.dayChangePercent != null
                    ? `${formatRupeeWithSign(etf.dayChange)} (${formatPercentageNoSign(etf.dayChangePercent)}) 1D`
                    : "-",

            dayChange: etf?.dayChange,
            dayChangePercent: etf?.growth ?? etf?.dayChangePercent,

            isPositive: (etf?.growth ?? etf?.dayChangePercent ?? etf?.dayChange) != null ? Number(etf?.growth ?? etf?.dayChangePercent ?? etf?.dayChange) >= 0 : null,

            closingPrice:
                etf?.closingPrice != null
                    ? `₹${formatIndianNumberWithDecimals(etf.closingPrice)}`
                    : "-",

            qty:
                etf?.quantity != null
                    ? formatIndianNumberWithDecimals(etf.quantity).split(".")[0]
                    : "-"
        }))
    };
};

/**
 * Parser for Investment Portfolio ETF API response.
 * Converts API schema to UI expected format for ETF listing page.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed ETF listing data for UI
 */
export const parseInvestmentPortfolioEtf = (data) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? null;
    const etfs = data?.etf ?? [];

    const items = etfs.map((etf, index) => {
        const dayChangePercent = etf?.growth ?? etf?.dayChangePercent;

        return {
            id: etf?.categoryId ?? index,
            name: etf?.name ?? "Unknown",
            brokerId: etf?.categoryId ?? "",
            accountId: etf?.accountId ?? "-",
            nomineeStatus: normalizeNomineeStatus(etf?.nomineeStatus),
            value: etf?.value != null ? `₹${formatIndianNumberWithDecimals(etf.value)}` : "-",
            changeValue: formatPercentageNoSign(dayChangePercent),
            changeLabel: "1D",
            isPositive: dayChangePercent != null ? Number(dayChangePercent) >= 0 : null,
            logo: getLogoUrl(etf),
        };
    });

    const totalValue = data?.totalValue;

    return {
        title: "ETF",
        subtitle: "Exchange traded funds",
        asOfDate: asOnDate ? `as on date ${formatAsOnDate(asOnDate).replace(";", "")}` : "-",
        totalValue: totalValue != null ? `₹${formatIndianNumberWithDecimals(totalValue)}` : "-",
        items,
    };
};

export const wealthChartData = [
    { category: "Investments", value: 0, color: "#783daa" },
    { category: "Banks", value: 0, color: "#b07edb" },
];
/**
 * Parser for Investment Portfolio INVIT API response.
 * Converts API schema to UI expected format for INVIT listing page.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed INVIT listing data for UI
 */
export const parseInvestmentPortfolioInvit = (data) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? null;
    const invits = data?.invit ?? [];

    const items = invits.map((invit, index) => {
        const dayChangePercent = invit?.growth ?? invit?.dayChangePercent;

        return {
            id: invit?.categoryId ?? index,
            name: invit?.name ?? "Unknown",
            brokerId: invit?.categoryId ?? "",
            accountId: invit?.accountId ?? "-",
            nomineeStatus: normalizeNomineeStatus(invit?.nomineeStatus),
            value: invit?.value != null ? `₹${formatIndianNumberWithDecimals(invit.value)}` : "-",
            changeValue: formatPercentageNoSign(dayChangePercent),
            changeLabel: "1D",
            isPositive: dayChangePercent != null ? Number(dayChangePercent) >= 0 : null,
            logo: getLogoUrl(invit),
        };
    });

    const totalValue = data?.totalValue;

    return {
        // title: "Infrastructure Investment Trusts (InvITs)",
        title: "Invits",
        // subtitle: "Infrastructure investment trusts regulated by SEBI",
        asOfDate: `as on date ${formatAsOnDate(asOnDate).replace(";", "")}`,
        totalValue: totalValue != null ? `₹${formatIndianNumberWithDecimals(totalValue)}` : "-",
        items,
    };
};

/**
 * Parser for Investment Portfolio INVIT details API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed INVIT details data for UI
 */
export const parseInvestmentPortfolioInvitDetails = (data) => {
    if (!data) return null;

    const marketCapColors = ["#8B5CF6", "#A855F7", "#D8B4FE"];
    const sectorColors = ["#6366F1", "#8B5CF6", "#A855F7", "#EC4899", "#3B82F6", "#F472B6"];

    return {
        name: data?.name ?? "Unknown",
        accountId: data?.accountId ?? "-",
        nomineeStatus: normalizeNomineeStatus(data?.nomineeStatus),

        value:
            (data?.value != null || data?.portfolioValue != null)
                ? `₹${formatIndianNumberWithDecimals(data?.value ?? data?.portfolioValue)}`
                : "-",

        changeValue:
            (data?.growth ?? data?.dayChangePercent) != null
                ? formatPercentage(data.growth ?? data.dayChangePercent)
                : "-",

        rawChangePercent: data?.growth ?? data?.dayChangePercent ?? 0,
        changeLabel: "1D",
        isPositive: Number(data?.growth ?? data?.dayChangePercent ?? 0) >= 0,

        marketCap: {
            totalAmount:
                data?.marketCapDistribution?.totalAmount != null
                    ? `₹${formatIndianNumberWithDecimals(data.marketCapDistribution.totalAmount)}`
                    : "-",

            data: (data?.marketCapDistribution?.data ?? []).map((item, index) => ({
                category: `${item?.type?.replace?.(/_/g, " ") ?? "Unknown"} - ₹${formatIndianNumberWithDecimals(item?.value ?? 0)}`,
                value: item?.percentage ?? 0,
                color: marketCapColors[index % marketCapColors.length]
            }))
        },

        sector: {
            totalAmount:
                data?.sectorDistribution?.totalAmount != null
                    ? `₹${formatIndianNumberWithDecimals(data.sectorDistribution.totalAmount)}`
                    : "-",

            data: (data?.sectorDistribution?.data ?? []).map((item, index) => ({
                category: `${item?.sector ?? "Unknown"} - ${item?.percentage ?? 0}%`,
                value: item?.percentage ?? 0,
                color: sectorColors[index % sectorColors.length]
            }))
        },

        holdings: (data?.holdings ?? []).map((invit) => ({
            name: invit?.name ?? "Unknown",
            isin: invit?.isin ?? "-",

            value:
                invit?.value != null
                    ? `₹${formatIndianNumberWithDecimals(invit.value)}`
                    : "-",

            change:
                invit?.dayChange != null && invit?.dayChangePercent != null
                    ? `${formatRupeeWithSign(invit.dayChange)} (${formatPercentageNoSign(invit.dayChangePercent)}) 1D`
                    : "-",

            dayChange: invit?.dayChange,
            dayChangePercent: invit?.growth ?? invit?.dayChangePercent,

            isPositive: (invit?.growth ?? invit?.dayChangePercent ?? invit?.dayChange) != null ? Number(invit?.growth ?? invit?.dayChangePercent ?? invit?.dayChange) >= 0 : null,

            closingPrice:
                invit?.closingPrice != null
                    ? `₹${formatIndianNumberWithDecimals(invit.closingPrice)}`
                    : "-",

            qty:
                invit?.quantity != null
                    ? formatIndianNumberWithDecimals(invit.quantity).split(".")[0]
                    : "-"
        }))
    };
};

/**
 * Parser for Investment Portfolio REIT API response.
 * Converts API schema to UI expected format for REIT listing page.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed REIT listing data for UI
 */
export const parseInvestmentPortfolioReit = (data) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? null;
    const reits = data?.reit ?? [];

    const items = reits.map((reit, index) => {
        const dayChangePercent = reit?.growth ?? reit?.dayChangePercent;

        return {
            id: reit?.categoryId ?? index,
            name: reit?.name ?? "Unknown",
            brokerId: reit?.categoryId ?? "",
            accountId: reit?.accountId ?? "-",
            nomineeStatus: normalizeNomineeStatus(reit?.nomineeStatus),
            value: reit?.value != null ? `₹${formatIndianNumberWithDecimals(reit.value)}` : "-",
            changeValue: formatPercentageNoSign(dayChangePercent),
            changeLabel: "1D",
            isPositive: dayChangePercent != null ? Number(dayChangePercent) >= 0 : null,
            logo: getLogoUrl(reit),
        };
    });

    const totalValue = data?.totalValue;

    return {
        title: "REITS",
        asOfDate: `as on date ${formatAsOnDate(asOnDate).replace(";", "")}`,
        totalValue: totalValue != null ? `₹${formatIndianNumberWithDecimals(totalValue)}` : "-",
        items,
    };
};

/**
 * Parser for Investment Portfolio REIT details API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed REIT details data for UI
 */
export const parseInvestmentPortfolioReitDetails = (data) => {
    if (!data) return null;

    return {
        name: data?.name ?? "Unknown",
        accountId: data?.accountId ?? "-",
        nomineeStatus: normalizeNomineeStatus(data?.nomineeStatus),

        value:
            data?.value != null
                ? `₹${formatIndianNumberWithDecimals(data.value)}`
                : "-",

        changeValue:
            (data?.growth ?? data?.dayChangePercent) != null
                ? formatPercentage(data.growth ?? data.dayChangePercent)
                : "-",

        changeLabel: "1D",
        isPositive: (data?.growth ?? data?.dayChangePercent) != null ? Number(data?.growth ?? data?.dayChangePercent) >= 0 : null,

        // REIT details normally won't show distribution charts in the current design,
        // so we'll leave them empty unless the API provides them.
        // marketCap: {
        //     totalAmount: "0",
        //     data: []
        // },
        // sector: {
        //     totalAmount: "0",
        //     data: []
        // },

        holdings: (data?.holdings ?? []).map((reit) => ({
            name: reit?.name ?? "Unknown",
            isin: reit?.isin ?? "-",

            value:
                reit?.value != null
                    ? `₹${formatIndianNumberWithDecimals(reit.value)}`
                    : "-",

            change:
                reit?.dayChange != null && reit?.dayChangePercent != null
                    ? `${formatRupeeWithSign(reit.dayChange)} (${formatPercentageNoSign(reit.dayChangePercent)}) 1D`
                    : "-",

            dayChange: reit?.dayChange,
            dayChangePercent: reit?.growth ?? reit?.dayChangePercent,

            isPositive: (reit?.growth ?? reit?.dayChangePercent ?? reit?.dayChange) != null ? Number(reit?.growth ?? reit?.dayChangePercent ?? reit?.dayChange) >= 0 : null,

            closingPrice:
                reit?.ltp != null
                    ? `₹${formatIndianNumberWithDecimals(reit.ltp)}`
                    : "-",

            qty:
                reit?.quantity != null
                    ? formatIndianNumberWithDecimals(reit.quantity).split(".")[0]
                    : "-"
        }))
    };
};

/**
 * Parser for Investment Portfolio NPS API response.
 *
 * @param {Object} data - API response result object
 * @returns {Object|null} Parsed NPS data for UI
 */
export const parseInvestmentPortfolioNps = (data) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? null;

    const parseTier = (tierData, label) => {
        if (!tierData) return null;
        return {
            label: label,
            totalValue: tierData?.totalValue != null ? `₹${formatIndianNumberWithDecimals(tierData.totalValue)}` : "-",
            accounts: (tierData?.accounts || []).map((acc, index) => ({
                id: index,
                scheme: acc?.scheme || "-",
                schemePreference: acc?.schemePreference || "-",
                currentAmount: acc?.currentAmount != null ? `₹${formatIndianNumberWithDecimals(acc.currentAmount)}` : "-",
                freeUnits: acc?.freeUnits ?? "-",
                lockedUnits: acc?.lockedUnits ?? "-",
                totalUnits: acc?.totalUnits ?? "-"
            }))
        };
    };

    return {
        title: "NPS",
        asOfDate: asOnDate ? `as on date ${formatAsOnDate(asOnDate).replace(";", "")}` : "-",
        tier1: parseTier(data?.tier1, "NPS - Tier 1"),
        tier2: parseTier(data?.tier2, "NPS - Tier 2")
    };
};

/**
 * Parser for Investment Portfolio FD/RD API response.
 */
export const parseInvestmentPortfolioFDRD = (data) => {
    if (!data) return null;

    const asOnDate = data?.asOnDate ?? null;

    const parseDeposits = (deposits) => {
        return (deposits || []).map(deposit => ({
            id: deposit.id || "",
            accountNumber: deposit.accountNumber || "-",
            investmentAmount: (deposit.investedAmount ?? deposit.investmentAmount) != null ? `₹${formatIndianNumberWithDecimals(deposit.investedAmount ?? deposit.investmentAmount)}` : "-",
            payoutAmount: deposit.payoutAmount != null ? `₹${formatIndianNumberWithDecimals(deposit.payoutAmount)}` : "-",
            payFrequency: deposit.payFrequency || "-",
            roi: deposit.roi != null ? `${deposit.roi.toFixed(2)}%` : "-",
            maturityDate: deposit.maturityDate ? formatDate(deposit.maturityDate, 'DD MMM YYYY') : "-",
            maturityAmount: deposit.maturityAmount != null ? `₹${formatIndianNumberWithDecimals(deposit.maturityAmount)}` : "-",
            asOnDateText: asOnDate ? `as on date ${formatAsOnDate(asOnDate)}` : "-",
            logo: (deposit.id && typeof deposit.id === 'string' && deposit.id.startsWith("FD"))
                ? "https://cdn-icons-png.flaticon.com/512/2830/2830284.png"
                : "https://cdn-icons-png.flaticon.com/512/2830/2830284.png" // Placeholder logo
        }));
    };

    return {
        asOfDate: asOnDate ? `as on date ${formatAsOnDate(asOnDate)}` : "-",
        fd: {
            title: "Fixed Deposit",
            totalValue: data?.fd?.totalValue != null ? `₹${formatIndianNumberWithDecimals(data.fd.totalValue)}` : "-",
            deposits: parseDeposits(data?.fd?.deposits)
        },
        rd: {
            title: "Recurring Deposit",
            totalValue: data?.rd?.totalValue != null ? `₹${formatIndianNumberWithDecimals(data.rd.totalValue)}` : "-",
            deposits: parseDeposits(data?.rd?.deposits)
        }
    };
};

/**
 * Parser for Investment Portfolio FD/RD Details API response.
 */
export const parseInvestmentPortfolioFDRDDetails = (data) => {
    if (!data) return null;

    return {
        accountNumber: data.accountNumber || "-",
        payFrequency: data.payFrequency || "-",
        payoutAmount: data.payoutAmount != null ? `₹${formatIndianNumberWithDecimals(data.payoutAmount)}` : "-",
        roi: data.roi != null ? `${data.roi}%` : "-",
        maturityDate: data.maturityDate ? formatDate(data.maturityDate, 'DD MMM YYYY') : "-",
        maturityAmount: data.maturityAmount != null ? `₹${formatIndianNumberWithDecimals(data.maturityAmount)}` : "-",
        investmentAmount: (data.investedAmount ?? data.investmentAmount) != null ? `₹${formatIndianNumberWithDecimals(data.investedAmount ?? data.investmentAmount)}` : "-",
        logo: data.logo || (data.accountNumber && typeof data.accountNumber === 'string' && data.accountNumber.startsWith("FD") ? "https://cdn-icons-png.flaticon.com/512/2830/2830284.png" : "https://cdn-icons-png.flaticon.com/512/2830/2830284.png"),
        transactions: (data.transactions || []).map((tx, index) => ({
            id: index,
            date: tx.date ? formatDate(tx.date, 'DD MMM YYYY') : "-",
            time: tx.time || "-",
            description: tx.description || "-",
            amount: tx.amount != null ? formatIndianNumberWithDecimals(tx.amount) : "-"
        }))
    };
};

